import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { ContextType } from "../../ProjectWrapper";
import React, { useContext, useState } from "react";
import { Box, Collapse, FormControlLabel, Link, Radio, RadioGroup, TextField, useTheme } from "@mui/material";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import { Subscription } from "../../../../models/Subscription";
import { ApiClient } from "../../../../client/ApiClient";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { Width } from "../../../../const/Width";
import { FormButtons } from "../../../../components/form/FormButtons";
import { SubscriptionFeatureTooltip } from "../../../../components/Tooltip/Tooltip";
import { SecurityNotifications } from "./SecurityNotifications";
import { SettingsSwitchOptionWrapper } from "../../../../components/form/OptionsWrapper";

interface State {
  host?: string
  port?: string
  user?: string
  password?: string
  from_address?: string
  hostError?: string
  portError?: string
  userError?: string
  passwordError?: string
  emailDelivery: "by_hanko" | "custom_smtp" | "off"
}

interface LoaderData {
  subscription: Subscription | null
}

export const Smtp = () => {
  const { project, organization } = useOutletContext() as ContextType
  const [loading, setIsLoading] = useState(false)
  const [state, setState] = useState<State>({
    host: project.smtp?.host ?? "",
    port: project.smtp?.port ?? "",
    user: project.smtp?.user ?? "",
    password: project.smtp?.password ?? "",
    from_address: project.email_from_address,
    emailDelivery: project.email_delivery_enabled ? project.smtp?.enabled === true ? "custom_smtp" : "by_hanko" : "off",
  })
  const loaderData = useLoaderData() as LoaderData
  const revalidator = useRevalidator()
  const { error, success } = useContext(SnackbarContext)
  const theme = useTheme()

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsLoading(true)

    if (state.emailDelivery === "custom_smtp" && (state.host === undefined || state.host.trim().length === 0)) {
      setState({
        ...state,
        hostError: "host must not be empty",
      })
      setIsLoading(false)
      return
    }
    if (state.emailDelivery === "custom_smtp" && (state.port === undefined || state.port.trim().length === 0)) {
      setState({
        ...state,
        portError: "port must not be empty",
      })
      setIsLoading(false)
      return
    }
    if (state.emailDelivery === "custom_smtp" && (state.user === undefined || state.user.trim().length === 0)) {
      setState({
        ...state,
        userError: "user must not be empty",
      })
      setIsLoading(false)
      return
    }
    if (state.emailDelivery === "custom_smtp" && (state.password === undefined || state.password.trim().length === 0)) {
      setState({
        ...state,
        passwordError: "password must not be empty",
      })
      setIsLoading(false)
      return
    }

    try {
      await ApiClient.updateProject(organization.id, project.id, {
        smtp: {
          enabled: state.emailDelivery === "custom_smtp",
          email_from_address: state.from_address,
          smtp: {
            host: state.host,
            port: state.port,
            user: state.user,
            password: state.password
          }
        },
        email_delivery_enabled: state.emailDelivery !== "off"
      })
      success("Settings updated")
      revalidator.revalidate()
    } catch (e) {
      error("failed to update settings")
    }
    setIsLoading(false)
  }

  const onCancel = () => {
    setState({
      ...state,
      host: project.smtp?.host ?? "",
      port: project.smtp?.port ?? "",
      user: project.smtp?.user ?? "",
      password: project.smtp?.password ?? "",
      from_address: project.email_from_address,
      emailDelivery: project.email_delivery_enabled ? project.smtp?.enabled === true ? "custom_smtp" : "by_hanko" : "off",
    })
  }

  const somethingChanged = () => {
    return state.host !== (project.smtp?.host ?? "") ||
      state.port !== (project.smtp?.port ?? "") ||
      state.user !== (project.smtp?.user ?? "") ||
      state.password !== (project.smtp?.password ?? "") ||
      state.from_address !== project.email_from_address ||
      state.emailDelivery !== (project.email_delivery_enabled ? project.smtp?.enabled === true ? "custom_smtp" : "by_hanko" : "off")
  }

  const hasSubscription = loaderData.subscription !== null && loaderData.subscription.is_active

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    maxWidth: Width.NORMAL,
    gap: 5
  }}>
    <SecurityNotifications organization={organization} project={project} />
    <form onSubmit={onSubmit}>
      <SettingsSwitchOptionWrapper sx={{
        borderBottom: `1px solid ${theme.palette.grey["900"]}`
      }}>
      <SettingsHead title="Email delivery"
                    description="Configure how Hanko emails like passcodes will be sent."
                    sx={{ mb: 1 }}
                    organization={organization} type="feature" plan="Pro" hasActiveSubscription={hasSubscription} />
      </SettingsSwitchOptionWrapper>
      <RadioGroup value={state.emailDelivery} onChange={onChange} name="emailDelivery">
        <FormControlLabel sx={{ my: { xs: 1, md: 0 } }} control={<Radio />}
                          label={<><span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>By Hanko</span> - All
                            email will be sent by a Hanko Cloud email server</>} value="by_hanko" />
        <SubscriptionFeatureTooltip openable={!hasSubscription}>
          <FormControlLabel sx={{ my: { xs: 1, md: 0 } }} control={<Radio />}
                            label={<><span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Custom SMTP</span> -
                              Configure your own SMTP server for email sending</>} value="custom_smtp"
                            disabled={!hasSubscription} />
        </SubscriptionFeatureTooltip>
        <Collapse in={state.emailDelivery === "custom_smtp"} timeout={{
          appear: 0,
          enter: 300,
          exit: 500,
        }} unmountOnExit>
          <Box display="flex" flexDirection="column" sx={{ gap: 3, my: 1, ml: 3 }}>
            <Box display="flex" flexDirection="row" sx={{ gap: 3 }}>
              <TextField required label="Host" name="host" placeholder="example.com"
                         onChange={onChange}
                         value={state.host}
                         error={state.hostError !== undefined}
                         sx={{ width: "70%" }} InputLabelProps={{ shrink: true }} />
              <TextField required label="Port" name="port" placeholder="465" onChange={onChange}
                         value={state.port}
                         error={state.portError !== undefined}
                         sx={{ width: "30%" }} InputLabelProps={{ shrink: true }} />
            </Box>
            <TextField required fullWidth label="Username" name="user" onChange={onChange}
                       value={state.user} error={state.userError !== undefined} InputLabelProps={{ shrink: true }}
                       placeholder="user" />
            <TextField required fullWidth type="password" label="Password" name="password"
                       onChange={onChange} value={state.password} error={state.passwordError !== undefined}
                       InputLabelProps={{ shrink: true }} placeholder="password" />
            <TextField required fullWidth type="email" label="Sender email address"
                       name="from_address"
                       placeholder="mail@example.com"
                       onChange={onChange}
                       value={state.from_address} />
          </Box>
        </Collapse>
        <SubscriptionFeatureTooltip openable={!hasSubscription}>
          <FormControlLabel sx={{ my: { xs: 1, md: 0 } }} control={<Radio />}
                            label={<><span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Off</span> - No emails
                              will be sent by Hanko. Use the <span style={{
                                fontFamily: "IBM Plex mono, monospace",
                                fontSize: '0.875rem',
                                fontWeight: 'bold'
                              }}>email.send</span> webhook to build your own email sending logic (<Link
                                href="https://docs.hanko.io/guides/email-delivery/email-delivery"
                                target="_blank">Docs</Link>)</>} value="off" disabled={!hasSubscription} />
        </SubscriptionFeatureTooltip>
      </RadioGroup>
      <FormButtons onCancel={onCancel} loading={loading} disabled={!somethingChanged()} sx={{ mt: 5 }} />
    </form>
  </Box>
}